/**
 * Chapter 6 — you always win: WARM UP's lane through five stages (generated
 * → one pinned by hand → one skipped → re-generated, both survive → reset),
 * with the timeline's own pill words. Reduced motion shows the stages
 * side by side.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Hand, Zap } from 'lucide-react';

import type { DayPlanDropRow } from '@trafficpulse/shared';

import { minutesLabel } from '@/features/drop-generator/utils/format';

import { HUMAN_WINS_STAGES, warmUpAt, type HumanWinsStage } from '../../fixtures';
import { Sweep } from '../../motion/Accents';
import { FrameStack } from '../../motion/FrameStack';
import { Illustration } from '../../motion/Illustration';

const CYCLE = 15;
/** The timeline row's own words for a human-owned draft. */
export const PINNED_COPY = 'pinned — your edits win (today only)';
export const SKIPPED_COPY = "skipped today — won't recreate";

const Row: FC<{ drop: DayPlanDropRow }> = ({ drop }) => {
  const r = drop.runtime;
  return (
    <div className={`flex items-center gap-2 rounded border px-2 py-1 font-mono text-xs ${
      r?.tombstoned ? 'border-dashed border-warning/60 text-muted-foreground line-through' : r?.pinned ? 'border-primary/50 bg-primary/5' : 'border-border/60 bg-background'
    }`}>
      <span className="w-12 text-muted-foreground">{minutesLabel(drop.minute)}</span>
      <span className="tabular-nums">{drop.amount.toLocaleString('en-US')}</span>
      <span className="min-w-0 grow truncate font-sans text-[11px] text-muted-foreground no-underline">
        {r?.tombstoned ? SKIPPED_COPY : r?.pinned ? PINNED_COPY : 'draft'}
      </span>
      {r?.pinned ? <Hand className="h-3 w-3 shrink-0 text-primary" /> : null}
    </div>
  );
};

const Lane: FC<{ stage: HumanWinsStage; index: number; sweep?: boolean }> = ({ stage, index, sweep }) => (
  <div className="relative flex flex-col gap-1 overflow-hidden rounded-lg border border-border/60 p-2">
    <p className="flex items-center gap-1.5 text-[11px] font-semibold">
      <span className="font-mono text-muted-foreground">{index + 1}.</span>
      {index === 0 || index === 3 ? <Zap className="h-3 w-3 text-primary" /> : <Hand className="h-3 w-3 text-primary" />}
      {HUMAN_WINS_STAGES[index].label}
    </p>
    {warmUpAt(stage).map((d) => <Row key={d.slotId} drop={d} />)}
    {sweep ? <Sweep at={0} /> : null}
  </div>
);

export const HumanWinsIllustration: FC = () => (
  <Illustration
    cycleSeconds={CYCLE}
    finalFrame={
      <div className="grid gap-2 md:grid-cols-2">
        {HUMAN_WINS_STAGES.slice(1).map((s, i) => <Lane key={s.stage} stage={s.stage} index={i + 1} />)}
      </div>
    }
  >
    <FrameStack
      cycleSeconds={CYCLE}
      frames={HUMAN_WINS_STAGES.map((s, i) => <Lane key={s.stage} stage={s.stage} index={i} sweep={s.stage === 'resynced'} />)}
    />
    <p className="mt-2 text-[11px] text-muted-foreground">
      the plan (⚡) lays the day out; your hand (✋) pins, skips and resets — the plan never undoes you
    </p>
  </Illustration>
);
